/**
 * Qué dicen las notas de filtro del Nodo de Datos (`utils/filterNotes.js`) sobre UN panel del
 * informe: cuáles de sus métricas no aplican (y por qué), y qué notas van debajo del valor.
 *
 * El Backend reenvía las notas por panel como `filter_notes`. Se aceptan las dos ubicaciones
 * posibles —al lado de `data` (`rendered.filter_notes`) o adentro (`rendered.data.filter_notes`)—
 * porque el contrato dice «en `filter_notes` de cada panel» y las dos lecturas son válidas: leer
 * las dos evita que un desacuerdo de una clave deje al usuario viendo un 0 como si fuera dato.
 *
 * Las de la consulta de TOTALES (el Total de la tabla, la tarjeta) vienen aparte, en
 * `summary_filter_notes`, sólo cuando dicen otra cosa que las de la tabla —incluso nada, `{}`— (card
 * x4TLNeOw): si la clave está, manda ella; si no, valen las de la tabla.
 *
 * Puro (sin React), para testearlo. **Sin notas devuelve `null`** y las grillas dibujan lo de siempre.
 */
import {AUDIENCE, isNotApplicable, normalizeFilterNotes, notApplicableMessage, notesForMetrics} from '../../../utils/filterNotes';
import {PANEL_TYPES, tableColumnsOf} from './informeModel';

/** Las notas crudas del panel, donde el Backend las haya puesto; `null` si no hay. */
export function rawPanelFilterNotes(rendered) {
    if (!rendered) return null;
    if (rendered.filter_notes) return rendered.filter_notes;
    if (rendered.data && rendered.data.filter_notes) return rendered.data.filter_notes;
    return null;
}

/**
 * Las notas crudas de los TOTALES del panel. `undefined` si la clave no está (valen las de la tabla);
 * el valor tal cual si está, aunque sea `{}` («los totales no tienen notas»).
 */
export function rawPanelSummaryFilterNotes(rendered) {
    const holders = rendered ? [rendered, rendered.data] : [];
    const holder = holders.find((h) => h && Object.prototype.hasOwnProperty.call(h, 'summary_filter_notes'));
    return holder ? holder.summary_filter_notes : undefined;
}

/** Las métricas que MUESTRA el panel: lo que devolvió el motor y, si no, su configuración. */
export function panelMetricIds(panel, rendered, fields) {
    const data = rendered && rendered.data;
    if (data && Array.isArray(data.metrics) && data.metrics.length) return data.metrics;
    if (data && data.metric) return [data.metric];
    if (!panel) return [];
    if (panel.type === PANEL_TYPES.TABLE && fields) return tableColumnsOf(panel, fields).metrics;
    return panel.metrics || [];
}

/** Los «no aplica» de unas notas para unas métricas: `{metricId: motivo}`. */
function notApplicableOf(raw, metrics, t, fieldsData) {
    const notApplicable = {};
    metrics.forEach((metric) => {
        if (isNotApplicable(raw, metric)) notApplicable[metric] = notApplicableMessage(raw, metric, t, fieldsData);
    });
    return notApplicable;
}

/**
 * @returns `null` sin notas; si no:
 *  - `raw`: las notas de la tabla tal cual, para la tabla (que las resuelve por columna);
 *  - `summaryRaw`: las de los totales tal cual (`undefined` si el Backend no mandó la clave);
 *  - `notApplicable`: `{metricId: motivo}` sólo de las métricas de este panel;
 *  - `notApplicableMetrics`: sus ids, en el orden del panel;
 *  - `allNotApplicable`: el panel entero no se puede calcular con estos filtros;
 *  - `notes`: las notas escritas (parciales, sin atribuir) de las métricas del panel;
 *  - `texts`: SÓLO los motivos del «no aplica», sin repetir, para el ⓘ del encabezado. Las notas
 *    van escritas debajo del valor (`notes`) o en el ⓘ de la columna de la tabla; en el ⓘ del bloque
 *    repetían lo que ya se leía debajo (QA-VIS-filtrosfe-25).
 * La tarjeta muestra el summary, así que se resuelve con las notas de los totales. La tabla pinta sus
 * celdas con las de la tabla y su Total con las de los totales: su ⓘ junta los motivos de las dos.
 * `audience`: `AUDIENCE.PORTAL` cuando lo lee el cliente (la nota del costo sin atribuir no le pide
 * revisar las UTM: es trabajo de la agencia).
 */
export function panelFilterNotes(panel, rendered, fields, fieldsData, t, audience = AUDIENCE.AGENCY) {
    const tableRaw = rawPanelFilterNotes(rendered);
    const summaryRaw = rawPanelSummaryFilterNotes(rendered);
    const totalsRaw = summaryRaw !== undefined ? summaryRaw : tableRaw;
    const isKpi = !!panel && panel.type === PANEL_TYPES.KPI;
    const isTable = !!panel && panel.type === PANEL_TYPES.TABLE;
    const ownRaw = isKpi ? totalsRaw : tableRaw;
    const withTotals = isTable && summaryRaw !== undefined;
    if (!normalizeFilterNotes(ownRaw) && !(withTotals && normalizeFilterNotes(summaryRaw))) return null;

    const metrics = panelMetricIds(panel, rendered, fields);
    const notApplicable = notApplicableOf(ownRaw, metrics, t, fieldsData);
    const notApplicableMetrics = Object.keys(notApplicable);
    const notes = notesForMetrics(ownRaw, metrics, t, fieldsData, audience);
    const texts = notApplicableMetrics.map((m) => notApplicable[m]);
    if (withTotals) {
        texts.push(...Object.values(notApplicableOf(summaryRaw, metrics, t, fieldsData)));
        notes.push(...notesForMetrics(summaryRaw, metrics, t, fieldsData, audience).filter((n) => !notes.includes(n)));
    }
    // Sin repetir: `na_paid_on_ga4` y `na_store` no nombran la métrica, y dos métricas con el mismo
    // filtro daban el mismo texto dos veces (con key duplicada en el ⓘ).
    const uniqueTexts = [...new Set(texts.filter(Boolean))];
    // Con notas y sin motivos hay algo que dibujar debajo del valor, aunque el ⓘ no se dibuje.
    if (!uniqueTexts.length && !notes.length) return null;
    return {
        raw: tableRaw,
        summaryRaw,
        notApplicable,
        notApplicableMetrics,
        allNotApplicable: metrics.length > 0 && notApplicableMetrics.length === metrics.length,
        notes,
        texts: uniqueTexts,
    };
}
